import { API } from "config/api";
import { API_ROUTES } from "config/apiRoutes";
import {
  DomainOrganization,
  OrganizationDomain,
  OrganizationDomainCandidate,
} from "types/organizationDomain";

function organizationsArray(value: unknown): DomainOrganization[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((organization: any) => ({
      org_id: String(
        organization.org_id ??
          organization.organization_id ??
          organization.id ??
          "",
      ),
      name: String(organization.name ?? organization.organization_name ?? ""),
      logo_url: organization.logo_url ?? organization.logoUrl,
      type: organization.type ?? organization.organization_type,
    }))
    .filter((organization) => organization.org_id);
}

function normalizeDomain(value: any): OrganizationDomain {
  return {
    organization_domain_id: String(
      value.organization_domain_id ?? value.domain_id ?? value.id ?? "",
    ),
    name: String(value.name ?? value.domain_name ?? ""),
    created_at: value.created_at ?? value.createdAt,
    updated_at: value.updated_at ?? value.updatedAt,
    organizations: organizationsArray(
      value.organizations ?? value.orgs ?? value.members,
    ),
  };
}

function normalizeCandidate(
  value: any,
  index: number,
): OrganizationDomainCandidate {
  return {
    prefix: String(
      value.prefix ?? value.domain_prefix ?? value.name ?? `candidate-${index}`,
    ),
    suggested_name:
      value.suggested_name ?? value.suggestedName ?? value.domain_name,
    organizations: organizationsArray(
      value.organizations ?? value.orgs ?? value.members,
    ),
  };
}

export async function listOrganizationDomains(): Promise<OrganizationDomain[]> {
  const { data } = await API.get(API_ROUTES.ORGANIZATION_DOMAINS.LIST());
  const domains = Array.isArray(data)
    ? data
    : data?.domains ?? data?.organization_domains ?? data?.items ?? [];
  return Array.isArray(domains) ? domains.map(normalizeDomain) : [];
}

export async function lookupOrganizationDomainCandidates(
  name?: string,
): Promise<OrganizationDomainCandidate[]> {
  const { data } = await API.get(API_ROUTES.ORGANIZATION_DOMAINS.CANDIDATES(), {
    params: name ? { name } : undefined,
  });
  const candidates = Array.isArray(data)
    ? data
    : data?.candidates ?? data?.domains ?? data?.items ?? [];
  return Array.isArray(candidates) ? candidates.map(normalizeCandidate) : [];
}

export async function createOrganizationDomain(
  name: string,
  orgIds: string[],
): Promise<OrganizationDomain> {
  const { data } = await API.post(API_ROUTES.ORGANIZATION_DOMAINS.LIST(), {
    name,
    org_ids: orgIds,
  });
  return normalizeDomain(data?.domain ?? data?.organization_domain ?? data);
}

export async function updateOrganizationDomain(
  id: string,
  name: string,
  orgIds: string[],
): Promise<OrganizationDomain> {
  const { data } = await API.patch(API_ROUTES.ORGANIZATION_DOMAINS.DETAIL(id), {
    name,
    org_ids: orgIds,
  });
  return normalizeDomain(data?.domain ?? data?.organization_domain ?? data);
}

export async function deleteOrganizationDomain(id: string): Promise<void> {
  await API.delete(API_ROUTES.ORGANIZATION_DOMAINS.DETAIL(id));
}
